import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabase";
import { useActions } from "../context/actions";
import Seo from "../components/Seo";
import "../components/booth/tokens.css";
import BoothMark from "../components/booth/BoothMark";
import BoothField from "../components/booth/BoothField";
import Footer from "../components/booth/Footer";
import "./ResetPassword.css";

function ResetPassword() {
  const actions = useActions();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("form"); // "form", "submitting", "success", "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    const access_token = searchParams.get("access_token");
    const refresh_token = searchParams.get("refresh_token");

    if (!access_token || !refresh_token) {
      setStatus("error");
      setErrorMessage("Invalid password reset link");
      return;
    }

    supabase.auth
      .setSession({ access_token, refresh_token })
      .then(() => setStatus("form"))
      .catch(() => {
        setStatus("error");
        setErrorMessage("Invalid password reset link");
      });
  }, [searchParams]);

  const handlePasswordReset = async (event) => {
    event.preventDefault();

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setPasswordError("");
    setStatus("submitting");

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;

      setStatus("success");
      actions.setPasswordReset(true);
      setTimeout(() => navigate("/auth"), 2000);
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.message || "Password reset failed. Please try again.");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "form":
      case "submitting":
        return (
          <>
            <p className="booth-auth-eyebrow">Step inside</p>
            <h1 className="booth-auth-title">Reset your password</h1>
            <form onSubmit={handlePasswordReset} className="booth-auth-form" noValidate>
              <BoothField
                id="reset-new-password"
                label="New password"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                error={passwordError}
              />
              <BoothField
                id="reset-confirm-password"
                label="Confirm new password"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
              <button
                type="submit"
                className="booth-btn booth-btn--primary booth-auth-submit"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Resetting…" : "Reset password"}
              </button>
            </form>
          </>
        );

      case "success":
        return (
          <>
            <h1 className="booth-auth-title">Password updated.</h1>
            <p className="booth-reset-dek">
              You're all set — taking you to log in…
            </p>
          </>
        );

      case "error":
        return (
          <>
            <h1 className="booth-auth-title">We couldn't reset your password.</h1>
            <p className="booth-reset-dek">{errorMessage}</p>
            <button
              type="button"
              className="booth-btn booth-btn--primary booth-auth-submit"
              onClick={() => navigate("/auth")}
            >
              Back to log in <span aria-hidden="true">→</span>
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="booth booth-reset-page">
      <Seo
        title="Reset your password — The Booth"
        description="Set a new password for your Booth account."
        path="/resetPassword"
        noindex
      />
      <header className="booth-shell booth-reset-masthead">
        <a
          href="/"
          className="booth-reset-mark-link"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          <BoothMark />
        </a>
      </header>
      <main className="booth-shell booth-reset-main">
        <div className="booth-auth-card">{renderContent()}</div>
      </main>
      <Footer />
    </div>
  );
}

export default ResetPassword;
